import React, { type CSSProperties } from "react";
import { useNavigate } from "react-router";

export type AttentionSeverity = "crit" | "warn" | "info";

export interface AttentionItem {
  id: string;
  severity: AttentionSeverity;
  title: string;
  /** Where the item takes the admin (filtered page) */
  to: string;
  /** Short label of the destination page */
  destination: string;
  count?: number;
}

interface AttentionQueueProps {
  title: string;
  items: AttentionItem[];
  /** True only while nothing at all has resolved yet. */
  isLoading?: boolean;
  /** True while some sources are still loading but others have items to show. */
  isPartial?: boolean;
  partialText?: string;
  /** True when a source failed, so "all clear" would be a false statement. */
  hasError?: boolean;
  errorText?: string;
  emptyText: string;
}

const SEVERITY: Record<AttentionSeverity, { stripe: string; bg: string; color: string }> = {
  crit: { stripe: "#dc2626", bg: "#fee2e2", color: "#991b1b" },
  warn: { stripe: "#d97706", bg: "#fef3c7", color: "#92400e" },
  info: { stripe: "#4f46e5", bg: "#e0e7ff", color: "#3730a3" },
};

const ORDER: AttentionSeverity[] = ["crit", "warn", "info"];

/**
 * "Needs attention" list: each row is a condition with a count and a deep link.
 */
const AttentionQueue: React.FC<AttentionQueueProps> = ({
  title,
  items,
  isLoading,
  isPartial,
  partialText,
  hasError,
  errorText,
  emptyText,
}) => {
  const navigate = useNavigate();
  const sorted = [...items].sort((a, b) => ORDER.indexOf(a.severity) - ORDER.indexOf(b.severity));
  const critCount = items.filter((i) => i.severity === "crit").length;

  return (
    <div style={styles.panel}>
      <div style={styles.head}>
        <span style={styles.title}>{title}</span>
        {!isLoading && items.length > 0 && (
          <span style={{ ...styles.counter, background: critCount > 0 ? "#dc2626" : "#d97706" }}>{items.length}</span>
        )}
        {!isLoading && hasError && errorText && (
          <span style={{ ...styles.partial, color: "#b45309" }}>
            <i className="fas fa-exclamation-triangle mr-1" aria-hidden="true" />
            {errorText}
          </span>
        )}
        {!isLoading && !hasError && isPartial && partialText && (
          <span style={styles.partial}>
            <i className="fas fa-circle-notch fa-spin mr-1" aria-hidden="true" />
            {partialText}
          </span>
        )}
      </div>
      {isLoading ? (
        <div style={styles.list}>
          {[0, 1, 2].map((i) => (
            <div key={i} style={{ ...styles.skeleton, width: `${80 - i * 12}%` }} />
          ))}
        </div>
      ) : sorted.length === 0 ? (
        <div style={styles.empty} role={hasError ? "alert" : undefined}>
          {hasError ? (
            <>
              <i
                className="fas fa-exclamation-triangle"
                style={{ color: "#d97706", marginRight: 8 }}
                aria-hidden="true"
              />
              {errorText}
            </>
          ) : isPartial ? (
            <>
              <i className="fas fa-circle-notch fa-spin" style={{ color: "#6b7280", marginRight: 8 }} aria-hidden="true" />
              {partialText}
            </>
          ) : (
            <>
              <i className="fas fa-check-circle" style={{ color: "#059669", marginRight: 8 }} aria-hidden="true" />
              {emptyText}
            </>
          )}
        </div>
      ) : (
        <div style={styles.list}>
          {sorted.map((item) => {
            const sev = SEVERITY[item.severity];
            return (
              <div
                key={item.id}
                className="attention-row"
                style={{ ...styles.row, borderLeftColor: sev.stripe }}
                role="button"
                tabIndex={0}
                onClick={() => navigate(item.to)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(item.to);
                  }
                }}
              >
                <div style={styles.rowMain}>
                  {item.count !== undefined && <span style={{ ...styles.count, color: sev.stripe }}>{item.count}</span>}
                  <span style={styles.rowTitle}>{item.title}</span>
                </div>
                <span style={{ ...styles.dest, background: sev.bg, color: sev.color }}>
                  {item.destination}{" "}
                  <i className="fas fa-arrow-right" style={{ fontSize: 9, marginLeft: 4 }} aria-hidden="true" />
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, CSSProperties> = {
  panel: {
    background: "#fff",
    border: "1px solid #e5e7eb",
    borderRadius: 10,
    padding: "14px 16px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  head: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 },
  title: { fontWeight: 700, fontSize: 14, color: "#111827" },
  counter: {
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    borderRadius: 999,
    padding: "2px 8px",
    minWidth: 22,
    textAlign: "center",
  },
  list: { display: "flex", flexDirection: "column", gap: 6 },
  row: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    padding: "8px 10px 8px 12px",
    borderLeft: "3px solid",
    background: "#f9fafb",
    borderRadius: 6,
    cursor: "pointer",
  },
  rowMain: { display: "flex", alignItems: "baseline", gap: 8, minWidth: 0 },
  count: { fontWeight: 700, fontSize: 15, fontVariantNumeric: "tabular-nums", flexShrink: 0 },
  rowTitle: { fontSize: 13, color: "#1f2937" },
  dest: {
    fontSize: 11,
    fontWeight: 600,
    borderRadius: 4,
    padding: "2px 8px",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  empty: { fontSize: 13, color: "#6b7280", padding: "16px 0" },
  partial: { fontSize: 11, color: "#6b7280", fontWeight: 600 },
  skeleton: { height: 32, borderRadius: 6, background: "#f3f4f6" },
};

export default AttentionQueue;
